import cn from "clsx";
import React, { useId } from "react";
import { Text } from "../../text";
import styles from "../Table.module.scss";
import { TableCaption } from "./TableCaption";
import { TableRow } from "./TableRow";

const extractTextFromReactElement = (element: React.ReactNode): string => {
    if (typeof element === "string" || typeof element === "number") {
        return element.toString();
    }
    if (React.isValidElement(element) && element.props) {
        const children = element.props.children;
        if (Array.isArray(children)) {
            return children.map(extractTextFromReactElement).join("");
        }
        return extractTextFromReactElement(children);
    }
    return "";
};

type MobileTableProps = React.PropsWithChildren<{
    title: React.ReactNode;
    showTitle?: boolean;
    className?: string;
    isStrictStyle?: boolean;
    header: React.ReactElement<React.PropsWithChildren>;
    groupClassName?: string;
}>;

export const MobileTable: React.FC<MobileTableProps> = ({
    title,
    showTitle = true,
    header,
    children,
    isStrictStyle,
    className = "",
}) => {
    const mobileTitleId = useId();
    if (!children) {
        return null;
    }
    const table = React.Children.toArray(children) as React.ReactElement<{
        children: React.ReactNode;
        header: React.ReactNode;
    }>[];

    /**
     * The cell items to be used as row headers for mobile.
     * Previously, these were the column headers on desktop
     */
    const rowTitleItems = React.Children.toArray(header.props.children).slice(
        1,
    );

    const rowTitleHeader = React.Children.toArray(header.props.children)[0];
    const flattenChildren = (children: React.ReactNode): React.ReactNode[] => {
        const flatChildren: React.ReactNode[] = [];
        React.Children.forEach(children, (child) => {
            if (
                React.isValidElement(child) &&
                Array.isArray(child.props?.children)
            ) {
                flatChildren.push(...flattenChildren(child.props.children));
            } else {
                flatChildren.push(child);
            }
        });
        return flatChildren;
    };
    /**
     * The cell items to be used as the "title" of each group in the table.
     */
    const groupHeaderItems = table.map((item) => {
        if (React.isValidElement(item)) {
            const flatChildren = flattenChildren(item.props.children);
            if (Array.isArray(flatChildren)) {
                return item.props.header ?? flatChildren[0];
            }
        }
        return [];
    });

    const cellItems = table.map((item) => {
        if (React.isValidElement(item)) {
            const flatChildren = flattenChildren(item.props.children);
            if (Array.isArray(flatChildren)) {
                return flatChildren.slice(item.props.header ? 0 : 1);
            }
        }
        return [];
    });

    return (
        <div
            className={styles["table-mobile-wrapper"]}
            aria-labelledby={mobileTitleId}
        >
            {showTitle && (
                <Text.Body
                    tag="div"
                    id={mobileTitleId}
                    className={styles["table-mobile-title"]}
                >
                    {title}
                </Text.Body>
            )}
            {groupHeaderItems.map((group, groupIndex) => {
                const groupProps = React.isValidElement(group)
                    ? group.props
                    : {};
                const { children, ...restProps } = groupProps;
                return (
                    <table
                        key={groupIndex}
                        className={cn(
                            styles.table,
                            styles["table--mobile"],
                            className,
                            groupProps.className,
                            {
                                [styles["table--strict"]]: isStrictStyle,
                            },
                        )}
                    >
                        <TableCaption>
                            {title && (
                                <span className="visually-hidden">
                                    {title}:{" "}
                                </span>
                            )}
                            {`${extractTextFromReactElement(rowTitleHeader)} ${
                                groupIndex + 1
                            }`}
                        </TableCaption>
                        <thead>
                            <TableRow>
                                <th colSpan={2} data-column="1">
                                    <Text.Body
                                        {...restProps}
                                        variant="2"
                                        tag="div"
                                    >
                                        {children}
                                    </Text.Body>
                                </th>
                            </TableRow>
                        </thead>
                        <tbody
                            className={cn(styles.body, "table__body", {
                                [styles["body--last"]]:
                                    groupIndex === groupHeaderItems.length - 1,
                            })}
                        >
                            {rowTitleItems.map((headerItem, headerIndex) => {
                                const headerItemProps = React.isValidElement(
                                    headerItem,
                                )
                                    ? headerItem.props
                                    : {};
                                const { children, ...restProps } =
                                    headerItemProps;
                                return (
                                    <TableRow
                                        key={headerIndex}
                                        className={cn(
                                            headerItemProps.className,
                                            {
                                                [styles["row--last"]]:
                                                    headerIndex ===
                                                    rowTitleItems.length - 1,
                                            },
                                        )}
                                    >
                                        <th
                                            scope="row"
                                            className={"table__column--1"}
                                        >
                                            <Text.Body
                                                {...restProps}
                                                variant="2"
                                                tag="span"
                                            >
                                                {children}
                                            </Text.Body>
                                        </th>
                                        {cellItems[groupIndex][headerIndex]}
                                    </TableRow>
                                );
                            })}
                        </tbody>
                    </table>
                );
            })}
        </div>
    );
};
